import {
  Box,
  Checkbox,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Input,
  Radio,
  RadioGroup,
  Stack,
  Text,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { NonPublicationReason, WithheldNoticeField, type WithheldFieldDto } from "../../../autogen/bff-api";
import { todayInOslo } from "./concludedDate";

export type WithheldChoices = Partial<Record<WithheldNoticeField, WithheldChoice>>;

interface WithheldChoice {
  reason?: NonPublicationReason;
  publishFrom: string;
  publishFromIncomplete?: boolean;
}

interface Props {
  fields: WithheldNoticeField[];
  choices: WithheldChoices;
  onChange: (choices: WithheldChoices) => void;
  designContest?: boolean;
}

export const WithheldFieldsSection = ({ fields, choices, onChange, designContest = false }: Props) => {
  const { t } = useTranslation();
  if (fields.length === 0) return null;
  const toggle = (field: WithheldNoticeField, withheld: boolean) => {
    const rest = Object.fromEntries(Object.entries(choices).filter(([key]) => key !== field)) as WithheldChoices;
    onChange(withheld ? { ...rest, [field]: { publishFrom: "" } } : rest);
  };
  const change = (field: WithheldNoticeField, update: Partial<WithheldChoice>) => {
    const current = choices[field];
    if (current) onChange({ ...choices, [field]: { ...current, ...update } });
  };
  return (
    <Box>
      <Text fontWeight="semibold">{t("Details not to be published")}</Text>
      <Text fontSize="sm" color="smMuted" mb="2">
        {designContest
          ? t(
              "Doffin receives these details, but does not publish them. Withhold a detail only when publishing it would cause one of the harms below (FOA § 31-2 (3), jf. § 21-6 (4), forsyningsforskriften § 27-2 (2), jf. § 17-6 (4)).",
            )
          : t(
              "Doffin receives these details, but does not publish them. Withhold a detail only when publishing it would cause one of the harms below (FOA § 21-6 (4), forsyningsforskriften § 17-6 (4)).",
            )}
      </Text>
      <Stack spacing="3">
        {fields.map((field) => (
          <WithheldFieldChoice
            key={field}
            field={field}
            choice={choices[field]}
            designContest={designContest}
            onToggle={(withheld) => toggle(field, withheld)}
            onChange={(update) => change(field, update)}
          />
        ))}
      </Stack>
    </Box>
  );
};

const WithheldFieldChoice = ({
  field,
  choice,
  designContest,
  onToggle,
  onChange,
}: {
  field: WithheldNoticeField;
  choice: WithheldChoice | undefined;
  designContest: boolean;
  onToggle: (withheld: boolean) => void;
  onChange: (update: Partial<WithheldChoice>) => void;
}) => {
  const { t } = useTranslation();
  const label = useFieldLabel(field, designContest);
  const dateId = `withheld-${field}-publish-from`;
  const dateInPast = publishFromProblem(choice?.publishFrom ?? "", todayInOslo());
  const dateIncomplete = choice?.publishFromIncomplete === true;
  const readDate = (input: HTMLInputElement) =>
    onChange({ publishFrom: input.value, publishFromIncomplete: input.validity.badInput });
  return (
    <Box border="1px solid" borderColor="smBorder" rounded="md" p="3">
      <Checkbox isChecked={choice != null} onChange={(e) => onToggle(e.target.checked)}>
        {t("Do not publish: {{field}}", { field: label })}
      </Checkbox>
      {choice != null && (
        <Stack spacing="3" mt="3" pl="6">
          <FormControl isRequired isInvalid={choice.reason == null}>
            <FormLabel fontSize="sm">{t("Why may it not be published?")}</FormLabel>
            <RadioGroup
              value={choice.reason ?? ""}
              onChange={(value) => onChange({ reason: value as NonPublicationReason })}
            >
              <Stack spacing="2" fontSize="sm">
                <Radio value={NonPublicationReason.LawEnforcement}>
                  {t("a. Publishing it would hinder the enforcement of rules")}
                </Radio>
                <Radio value={NonPublicationReason.PublicInterest}>
                  {t("a. Publishing it would otherwise be contrary to public interests")}
                </Radio>
                <Radio value={NonPublicationReason.CommercialInterests}>
                  {t(
                    "b. Publishing it would harm the legitimate economic interests of particular public or private undertakings",
                  )}
                </Radio>
                <Radio value={NonPublicationReason.FairCompetition}>
                  {t("c. Publishing it would harm competition between suppliers")}
                </Radio>
              </Stack>
            </RadioGroup>
            <FormErrorMessage>{t("Choose why it may not be published")}</FormErrorMessage>
          </FormControl>
          <FormControl isInvalid={dateInPast || dateIncomplete}>
            <FormLabel htmlFor={dateId} fontSize="sm">
              {t("May be published from (optional)")}
            </FormLabel>
            <Input
              id={dateId}
              type="date"
              backgroundColor="smBackground"
              min={dayAfter(todayInOslo())}
              value={choice.publishFrom}
              onChange={(e) => readDate(e.currentTarget)}
              onKeyUp={(e) => readDate(e.currentTarget)}
              onBlur={(e) => readDate(e.currentTarget)}
            />
            {dateIncomplete ? (
              <FormErrorMessage>{t("Enter a whole date, or clear the field")}</FormErrorMessage>
            ) : dateInPast ? (
              <FormErrorMessage>{t("The date must be after today")}</FormErrorMessage>
            ) : (
              <FormHelperText>{t("Leave it empty to keep the detail unpublished.")}</FormHelperText>
            )}
          </FormControl>
        </Stack>
      )}
    </Box>
  );
};

const useFieldLabel = (field: WithheldNoticeField, designContest: boolean): string => {
  const { t } = useTranslation();
  switch (field) {
    case WithheldNoticeField.ContractValue:
      return t("the contract value");
    case WithheldNoticeField.ReceivedTenders:
      return designContest ? t("the number of entries received") : t("the number of tenders received");
  }
};

const publishFromProblem = (publishFrom: string, today: string): boolean =>
  publishFrom !== "" && publishFrom <= today;

const dayAfter = (day: string): string => {
  const next = new Date(`${day}T00:00:00Z`);
  next.setUTCDate(next.getUTCDate() + 1);
  return next.toISOString().slice(0, 10);
};

export const withheldFieldsIncomplete = (choices: WithheldChoices): boolean => {
  const today = todayInOslo();
  return Object.values(choices).some(
    (choice) =>
      choice.reason == null || choice.publishFromIncomplete === true || publishFromProblem(choice.publishFrom, today),
  );
};

export const withheldFieldsRequest = (choices: WithheldChoices): WithheldFieldDto[] =>
  (Object.entries(choices) as [WithheldNoticeField, WithheldChoice][]).flatMap(([field, choice]) =>
    choice.reason == null
      ? []
      : [{ field, reason: choice.reason, publishFrom: choice.publishFrom === "" ? null : choice.publishFrom }],
  );
